import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "logo-wrapper" }
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "page-container" }
const _hoisted_4 = { class: "content-area" }
const _hoisted_5 = { class: "page-body-wrapper" }

import { ref, defineAsyncComponent, watch, onMounted, onBeforeUnmount, provide, computed } from 'vue';
import { useRoute } from "vue-router";
import { useMenuStore } from "@/store/menu";
import { uselayoutStore } from "@/store/layout";
import { storeToRefs } from "pinia";
import logoFallback from "@/assets/Logo.jpeg";
import logoCollapsedFallback from "@/assets/rtl.png";
import {
  loadBrandingFromGeneralSettings,
  getCachedBranding,
  hasConfiguredLogo,
  type BrandingSettings,
} from "@/utils/branding";
import { applyBrandingFavicon, applyCachedFaviconIfAny } from "@/utils/favicon";


export default /*@__PURE__*/_defineComponent({
  __name: 'bodyView',
  setup(__props) {

const footerView = defineAsyncComponent(() => import("@/layout/footerView.vue"));
const BreadCrumb = defineAsyncComponent(() => import("@/components/commen/block/header/BreadCrumb.vue"));
const sidebarSection = defineAsyncComponent(() => import("@/components/commen/block/sidebar/sidebarSection.vue"));

const route = useRoute();
const hideAppChrome = computed(() => route.meta.hideMenu === true || route.meta.hideFooter === true);

const branding = ref<BrandingSettings>(getCachedBranding());
const logosReady = ref(hasConfiguredLogo(getCachedBranding()));

const drawer = ref(true);

const headerSettings = ref<any[]>([]);
const hideDefaultSettings = ref(false); 
function registerHeaderSettings(items: any[], hideDefaults: boolean = false) {
  headerSettings.value = items;
  hideDefaultSettings.value = hideDefaults;
}

function clearHeaderSettings() {
  headerSettings.value = [];
  hideDefaultSettings.value = false;
}

provide('headerSettings', headerSettings);
provide('registerHeaderSettings', registerHeaderSettings);
provide('clearHeaderSettings', clearHeaderSettings);
provide('hideDefaultSettings', hideDefaultSettings);

// Check if RTL (Arabic language) - with reactive update
const isRTL = ref(
  document.documentElement.dir === 'rtl' ||
  document.documentElement.getAttribute('lang') === 'ar' ||
  localStorage.getItem('selectedLanguageCode') === 'ar'
);

// Function to check and update RTL status
const updateRTLStatus = () => {
  const newRTL = document.documentElement.dir === 'rtl' ||
                 document.documentElement.getAttribute('lang') === 'ar' ||
                 localStorage.getItem('selectedLanguageCode') === 'ar';

  if (newRTL !== isRTL.value) {
    isRTL.value = newRTL;
  }
};

// Create MutationObserver to watch for dir changes
let dirObserver: MutationObserver | null = null;

function getInitialDrawerWidth() {
  const screenWidth = window.innerWidth;
  const savedWidth = Number(localStorage.getItem("sidebar:width"));

  if (screenWidth < 768) {
    return 56;
  }

  if (screenWidth < 1024) {
    return 56;
  }

  return savedWidth || 250;
}
const drawerWidth = ref(getInitialDrawerWidth());
provide('drawerWidth', drawerWidth);

const activeLogoSrc = computed(() => {
  const collapsed = drawerWidth.value === 56;
  if (collapsed) {
    const configured =
      branding.value.logoCollapsed || branding.value.logoLtr || null;
    if (configured) return configured;
    return logosReady.value ? logoCollapsedFallback : null;
  }
  if (isRTL.value) {
    const configured = branding.value.logoRtl || branding.value.logoLtr || null;
    if (configured) return configured;
    return logosReady.value ? logoFallback : null;
  }
  if (branding.value.logoLtr) return branding.value.logoLtr;
  return logosReady.value ? logoFallback : null;
});

const onBrandingUpdated = (event: Event) => {
  const detail = (event as CustomEvent<BrandingSettings>).detail;
  if (detail) {
    branding.value = { ...detail };
    logosReady.value = true;
    applyBrandingFavicon(detail, true);
  }
};

const toggleDrawerWidth = () => {
  drawerWidth.value = drawerWidth.value === 56 ? 250 : 56;
  localStorage.setItem("sidebar:width", String(drawerWidth.value));
};

// Logo container style - للعربي فقط
const logoContainerStyle = computed(() => {
  const width = drawerWidth.value;
  if (isRTL.value) {
    return {
      right: '0',
      left: 'auto',
      width: width + 'px'
    };
  }
  return {
    left: '0',
    right: 'auto',
    width: width + 'px'
  };
});

// Computed styles based on RTL
const appBarStyle = computed(() => {
  if (isRTL.value) {
    return {
      right: drawerWidth.value + 'px !important',
      left: 'auto !important',
      width: `calc(100% - ${drawerWidth.value}px) !important`
    };
  }
  return {
    left: drawerWidth.value + 'px !important',
    right: 'auto !important',
    width: `calc(100% - ${drawerWidth.value}px) !important`
  };
});

const pageBodyStyle = computed(() => {
  if (hideAppChrome.value) {
    return { paddingRight: '0', paddingLeft: '0' };
  }
  if (isRTL.value) {
    return { paddingRight: drawerWidth.value + 'px', paddingLeft: '0' };
  }
  return { paddingLeft: drawerWidth.value + 'px', paddingRight: '0' };
});

const footerStyle = computed(() => {
  if (isRTL.value) {
    return { marginRight: drawerWidth.value + 'px', marginLeft: '0' };
  }
  return { marginLeft: drawerWidth.value + 'px', marginRight: '0' };
});

const handleScroll = () => {
  display.value = window.innerWidth <= 1199;

  const screenWidth = window.innerWidth;

  if (screenWidth < 1024 && drawerWidth.value > 56) {
    drawerWidth.value = 56;
    localStorage.setItem("sidebar:width", "56");
  }
};

const layoutobj = ref<any>({});
const sidebar_toggle_var = ref<boolean>(false);
const display = ref<boolean>(false);

const store = useMenuStore();
const storeLayout = uselayoutStore();
const { layout } = storeToRefs(storeLayout);

watch(
  () => layout.value,
  () => {
    layoutobj.value = storeLayout.layout.settings.sidebar_setting;
  },
  { deep: true }
);

watch(
  () => "router",
  () => {
    if (window.innerWidth < 991 && storeLayout.layout.settings.layout === 'Horizontal') {
      const newlayout = JSON.parse(JSON.stringify(layoutobj).replace('horizontal-wrapper', 'compact-wrapper'));
      layoutobj.value = JSON.parse(JSON.stringify(newlayout))[storeLayout.layout.settings.layout];
    } else {
      layoutobj.value = JSON.parse(JSON.stringify(layoutobj))[storeLayout.layout.settings.layout];
    }
  }
);

function sidebar_toggle(value: boolean) {
  sidebar_toggle_var.value = !value;
}

onMounted(() => {
  window.addEventListener('resize', handleScroll);
  window.addEventListener('branding-updated', onBrandingUpdated as EventListener);
  layoutobj.value = storeLayout.layout.settings.sidebar_setting;

  // Initial RTL check
  updateRTLStatus();

  applyCachedFaviconIfAny();

  void loadBrandingFromGeneralSettings().then((next) => {
    branding.value = { ...next };
    logosReady.value = true;
    applyBrandingFavicon(next, true);
  });

  // Setup MutationObserver to watch for dir attribute changes
  dirObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' &&
          (mutation.attributeName === 'dir' || mutation.attributeName === 'lang')) {
        updateRTLStatus();
      }
    });
  });

  dirObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['dir', 'lang']
  });

  // Watch for localStorage changes from same window
  const checkStorageChange = () => {
    updateRTLStatus();
  };
  window.addEventListener('storage', checkStorageChange);

  // Periodic check for same-window localStorage changes
  const storageInterval = setInterval(updateRTLStatus, 500);

  // Cleanup on unmount
  onBeforeUnmount(() => {
    if (dirObserver) {
      dirObserver.disconnect();
    }
    window.removeEventListener('storage', checkStorageChange);
    window.removeEventListener('branding-updated', onBrandingUpdated as EventListener);
    clearInterval(storageInterval);
  });

  handleScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleScroll);
  window.removeEventListener('branding-updated', onBrandingUpdated as EventListener);
});

return (_ctx: any,_cache: any) => {
  const _component_v_app_bar = _resolveComponent("v-app-bar")!
  const _component_router_view = _resolveComponent("router-view")!
  const _component_v_main = _resolveComponent("v-main")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, { class: "app-container" }, {
    default: _withCtx(() => [
      (!hideAppChrome.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["bg-logo position-fixed top-0", [isRTL.value ? 'logo-rtl' : 'logo-ltr']]),
            style: _normalizeStyle(logoContainerStyle.value)
          }, [
            _createElementVNode("div", _hoisted_1, [
              (activeLogoSrc.value)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    src: activeLogoSrc.value,
                    class: _normalizeClass(["logo-image", { 'logo-collapsed': drawerWidth.value === 56 }]),
                    alt: "Logo"
                  }, null, 10, _hoisted_2))
                : _createCommentVNode("", true)
            ])
          ], 6))
        : _createCommentVNode("", true),
      (!hideAppChrome.value)
        ? (_openBlock(), _createBlock(_component_v_app_bar, {
            key: 1,
            app: "",
            style: _normalizeStyle([{"z-index":"1000"}, appBarStyle.value]),
            "clipped-left": "",
            color: "white",
            elevation: "0"
          }, {
            prepend: _withCtx(() => [
              _createElementVNode("div", {
                class: "toggle-btn-wrapper",
                onClick: toggleDrawerWidth
              }, [...(_cache[1] || (_cache[1] = [
                _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "20",
                  height: "20",
                  viewBox: "0 0 20 20",
                  fill: "none"
                }, [
                  _createElementVNode("path", {
                    d: "M6.875 3.75V16.25H3.125C2.95924 16.25 2.80027 16.1842 2.68306 16.0669C2.56585 15.9497 2.5 15.7908 2.5 15.625V4.375C2.5 4.20924 2.56585 4.05027 2.68306 3.93306C2.80027 3.81585 2.95924 3.75 3.125 3.75H6.875Z",
                    fill: "black",
                    "fill-opacity": "0.04"
                  }),
                  _createElementVNode("path", {
                    d: "M16.875 3.125H3.125C2.79348 3.125 2.47554 3.2567 2.24112 3.49112C2.0067 3.72554 1.875 4.04348 1.875 4.375V15.625C1.875 15.9565 2.0067 16.2745 2.24112 16.5089C2.47554 16.7433 2.79348 16.875 3.125 16.875H16.875C17.2065 16.875 17.5245 16.7433 17.7589 16.5089C17.9933 16.2745 18.125 15.9565 18.125 15.625V4.375C18.125 4.04348 17.9933 3.72554 17.7589 3.49112C17.5245 3.2567 17.2065 3.125 16.875 3.125ZM3.125 11.875H4.375C4.54076 11.875 4.69973 11.8092 4.81694 11.6919C4.93415 11.5747 5 11.4158 5 11.25C5 11.0842 4.93415 10.9253 4.81694 10.8081C4.69973 10.6908 4.54076 10.625 4.375 10.625H3.125V9.375H4.375C4.54076 9.375 4.69973 9.30915 4.81694 9.19194C4.93415 9.07473 5 8.91576 5 8.75C5 8.58424 4.93415 8.42527 4.81694 8.30806C4.69973 8.19085 4.54076 8.125 4.375 8.125H3.125V6.875H4.375C4.54076 6.875 4.69973 6.80915 4.81694 6.69194C4.93415 6.57473 5 6.41576 5 6.25C5 6.08424 4.93415 5.92527 4.81694 5.80806C4.69973 5.69085 4.54076 5.625 4.375 5.625H3.125V4.375H6.25V15.625H3.125V11.875ZM16.875 15.625H7.5V4.375H16.875V15.625Z",
                    fill: "black"
                  })
                ], -1)
              ]))])
            ]),
            default: _withCtx(() => [
              _createVNode(_unref(BreadCrumb), {
                "extra-settings": headerSettings.value,
                "hide-default-settings": hideDefaultSettings.value
              }, null, 8, ["extra-settings", "hide-default-settings"])
            ]),
            _: 1
          }, 8, ["style"]))
        : _createCommentVNode("", true),
      _createVNode(_component_v_main, { class: "main-wrapper" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", {
                class: _normalizeClass(["page-wrapper", [display.value ? 'compact-sidebar compact-small material-icon' : layoutobj.value, { 'standalone-page': hideAppChrome.value }]]),
                id: "pageWrapper"
              }, [
                _createElementVNode("div", _hoisted_5, [
                  (!hideAppChrome.value)
                    ? (_openBlock(), _createBlock(_unref(sidebarSection), {
                        key: 0,
                        width: drawerWidth.value,
                        drawer: drawer.value,
                        "onUpdate:drawer": _cache[0] || (_cache[0] = ($event: any) => ((drawer).value = $event))
                      }, null, 8, ["width", "drawer"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", {
                    class: "page-body",
                    style: _normalizeStyle(pageBodyStyle.value)
                  }, [
                    _createElementVNode("div", null, [
                      _createVNode(_component_router_view, null, {
                        default: _withCtx(({ Component, route }) => [
                          _createVNode(_Transition, {
                            name: "route-fade",
                            mode: "out-in"
                          }, {
                            default: _withCtx(() => [
                              Component
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: route.fullPath,
                                    class: "route-view-root"
                                  }, [
                                    (_openBlock(), _createBlock(_resolveDynamicComponent(Component)))
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 1
                      })
                    ])
                  ], 4)
                ])
              ], 2)
            ]),
            (!hideAppChrome.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "footer-area",
                  style: _normalizeStyle(footerStyle.value)
                }, [
                  _createVNode(_unref(footerView))
                ], 4))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})